import { useState } from "react";
import { toast } from "sonner";
import { Check, MessageCircle, Gift } from "lucide-react";
import { Reveal } from "./Reveal";
import { WHATSAPP } from "../lib/i18n";

const UI = {
  it: {
    over: "Kit regalo · Perfetto per le feste", title: "Regala la cura,", title2: "confezionata da Gioia.",
    sub: "Scegli le creme Gioia Skincare da mettere nel kit: lo prepariamo con la shopper, il nastro e il biglietto firmati Gioia.",
    pick: "1 · Scegli le creme", note: "2 · Il biglietto", to: "Per", from: "Da", msg: "Dedica (facoltativa)",
    cta: "Richiedi il kit su WhatsApp", empty: "Scegli almeno una crema", count: (n) => `${n} ${n === 1 ? "crema" : "creme"} nel kit`,
    wa: ({ items, to, from, msg }) => [
      "Ciao Gioia, vorrei richiedere un kit regalo Gioia Skincare con:",
      ...items.map((x) => `- ${x}`),
      to && `Per: ${to}`, from && `Da: ${from}`, msg && `Dedica: "${msg}"`,
      "Mi fai sapere disponibilità e prezzo? Grazie!",
    ],
  },
  en: {
    over: "Gift kit · Perfect for the holidays", title: "Give the gift of care,", title2: "wrapped by Gioia.",
    sub: "Choose the Gioia Skincare creams for your kit: we prepare it with the Gioia bag, ribbon and gift card.",
    pick: "1 · Choose the creams", note: "2 · The gift card", to: "To", from: "From", msg: "Message (optional)",
    cta: "Request the kit on WhatsApp", empty: "Choose at least one cream", count: (n) => `${n} ${n === 1 ? "cream" : "creams"} in the kit`,
    wa: ({ items, to, from, msg }) => [
      "Hi Gioia, I'd like to request a Gioia Skincare gift kit with:",
      ...items.map((x) => `- ${x}`),
      to && `To: ${to}`, from && `From: ${from}`, msg && `Message: "${msg}"`,
      "Could you let me know availability and price? Thank you!",
    ],
  },
};

const field = "w-full bg-transparent border-0 border-b border-[#F6F2EC]/25 py-3 text-[#F6F2EC] outline-none transition-colors duration-300 focus:border-[#C9A66B] placeholder:text-[#8b8278]";

const Pick = ({ p, lang, on, toggle }) => (
  <button type="button" onClick={toggle} aria-pressed={on} data-testid={`kit-item-${p.slug}`}
    className={`group relative text-left flex sm:flex-col gap-4 items-center sm:items-stretch p-3 border transition-colors duration-300 ${on ? "border-[#C9A66B] bg-[#C9A66B]/10" : "border-[#F6F2EC]/15 hover:border-[#F6F2EC]/40"}`}>
    <div className="relative w-20 sm:w-full aspect-[4/5] sm:aspect-square shrink-0 overflow-hidden bg-[#ECE5DA]">
      <img src={p.img} alt={p[lang].name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
    </div>
    <span className="font-serif-d text-xl font-light text-[#F6F2EC] leading-tight pr-8 sm:pr-0">{p[lang].name}</span>
    <span className={`absolute top-3 right-3 sm:top-5 sm:right-5 w-7 h-7 rounded-full flex items-center justify-center border transition-colors duration-300 ${on ? "bg-[#C9A66B] border-[#C9A66B] text-[#14110F]" : "border-[#F6F2EC]/40 bg-[#14110F]/60 text-transparent"}`}>
      <Check className="w-4 h-4" />
    </span>
  </button>
);

export const GiftKit = ({ lang, products }) => {
  const u = UI[lang] ?? UI.it;
  const [sel, setSel] = useState(products.map((p) => p.slug));
  const [f, setF] = useState({ to: "", from: "", msg: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (slug) => setSel((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));

  const send = () => {
    if (!sel.length) { toast.error(u.empty); return; }
    const items = products.filter((p) => sel.includes(p.slug)).map((p) => p[lang].name);
    if (!items.length) { toast.error(u.empty); return; }
    const text = u.wa({ items, ...f }).filter(Boolean).join("\n");
    window.open(`${WHATSAPP}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <section id="kit-regalo" data-testid="gift-kit-section" className="scroll-mt-28 bg-[#14110F] grain text-[#F6F2EC] -mx-5 sm:mx-0 px-6 sm:px-12 lg:px-16 py-16 sm:py-20 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <Reveal><p className="overline text-[#C9A66B] flex items-center gap-3"><Gift className="w-4 h-4" /> {u.over}</p></Reveal>
        <h2 className="mt-6 font-serif-d font-light text-4xl sm:text-6xl leading-[0.98]">
          {u.title}<br /><span className="italic text-[#C9A66B]">{u.title2}</span>
        </h2>
        <p className="mt-6 text-[#d9d0c4] font-light leading-relaxed max-w-md">{u.sub}</p>
        <div className="mt-10 relative overflow-hidden aspect-[11/10] bg-[#1d1916]" data-testid="gift-kit-photo">
          <img src="/images/gift-kit-gioia.jpg" alt={lang === "en" ? "Gioia gift packaging with ribbon" : "Confezione regalo Gioia con nastro"} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        </div>
      </div>
      <div className="lg:col-span-7 space-y-10">
        <div>
          <div className="flex items-baseline justify-between gap-4 mb-5">
            <p className="overline text-[0.62rem] text-[#b8ad9f]">{u.pick}</p>
            <p className="text-xs text-[#C9A66B]" data-testid="kit-count">{u.count(products.filter((p) => sel.includes(p.slug)).length)}</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {products.map((p) => <Pick key={p.slug} p={p} lang={lang} on={sel.includes(p.slug)} toggle={() => toggle(p.slug)} />)}
          </div>
        </div>
        <div>
          <p className="overline text-[0.62rem] text-[#b8ad9f] mb-2">{u.note}</p>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-6">
            <input className={field} placeholder={u.to} value={f.to} onChange={set("to")} maxLength={40} data-testid="kit-to-input" />
            <input className={field} placeholder={u.from} value={f.from} onChange={set("from")} maxLength={40} data-testid="kit-from-input" />
            <input className={`${field} sm:col-span-2`} placeholder={u.msg} value={f.msg} onChange={set("msg")} maxLength={140} data-testid="kit-message-input" />
          </div>
        </div>
        <button type="button" onClick={send} data-testid="kit-request-btn"
          className="group w-full sm:w-auto inline-flex justify-center items-center gap-3 rounded-full bg-[#C9A66B] text-[#14110F] px-9 py-4 text-[0.75rem] tracking-[0.2em] uppercase font-medium transition-colors duration-500 hover:bg-[#F6F2EC]">
          <MessageCircle className="w-4 h-4" /> {u.cta}
        </button>
      </div>
    </section>
  );
};
